import { COLORS } from '@/config/colors';
import { css, fillOutlined, circle } from './draw-utils';
import type { ProceduralDef } from '../types';

const CROWN = 0x4a2f7a; // void-violet crown metal
const STAR = 0xe8dcff;

/**
 * B10 Null Crown — void Elemental. A floating crown of dark
 * shards around a singularity core. Frame 0 = open (core exposed), frame 1 =
 * shielded (event horizon disc swallows the core). docs/02 Elementals.
 */
export const bossNullBody: ProceduralDef = {
  key: 'boss/null/body',
  width: 140,
  height: 140,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 2,
  draw(ctx, w, h, frame) {
    const c = w / 2;
    const shielded = frame === 1;

    // Distortion halo — wider/brighter when the horizon is up
    const halo = ctx.createRadialGradient(c, c, 18, c, c, c);
    halo.addColorStop(0, shielded ? 'rgba(176,124,255,0.5)' : 'rgba(176,124,255,0.28)');
    halo.addColorStop(1, 'rgba(176,124,255,0)');
    circle(ctx, c, c, c);
    ctx.fillStyle = halo;
    ctx.fill();

    // Crown of shards (8 spikes around the core)
    const spikes = 8;
    for (let i = 0; i < spikes; i++) {
      const a = (Math.PI * 2 * i) / spikes - Math.PI / 2;
      const inR = 30;
      const outR = 52;
      const wsp = 0.18;
      ctx.beginPath();
      ctx.moveTo(c + Math.cos(a - wsp) * inR, c + Math.sin(a - wsp) * inR);
      ctx.lineTo(c + Math.cos(a) * outR, c + Math.sin(a) * outR);
      ctx.lineTo(c + Math.cos(a + wsp) * inR, c + Math.sin(a + wsp) * inR);
      ctx.closePath();
      fillOutlined(ctx, CROWN, 4);
    }

    // Core ring
    circle(ctx, c, c, 30);
    fillOutlined(ctx, COLORS.voidCore, 5);

    if (shielded) {
      // Event horizon — glossy black disc with a violet rim swallowing all
      circle(ctx, c, c, 28);
      ctx.fillStyle = '#0a0512';
      ctx.fill();
      ctx.strokeStyle = css(COLORS.voidGlow);
      ctx.lineWidth = 3;
      circle(ctx, c, c, 26);
      ctx.stroke();
      circle(ctx, c, c, 16);
      ctx.strokeStyle = 'rgba(176,124,255,0.5)';
      ctx.lineWidth = 2;
      ctx.stroke();
    } else {
      // Open core — starry singularity with a bright pinpoint
      circle(ctx, c, c, 24);
      ctx.fillStyle = '#12081f';
      ctx.fill();
      for (const [ox, oy, r] of [[-8, -6, 2], [9, -3, 1.6], [3, 9, 2], [-5, 7, 1.4]] as const) {
        circle(ctx, c + ox, c + oy, r);
        ctx.fillStyle = css(STAR);
        ctx.fill();
      }
      circle(ctx, c, c, 5);
      ctx.fillStyle = css(STAR);
      ctx.fill();
    }
    void h;
  },
};
